import { ACCESS_TREE_PATH_DELIMITER } from "../../common/constants";
import type { UUID } from "../../typings/global";
import type { AgentBase, AgentProfile } from "../agent/types";
import { EnumerationType } from "./enums";
import type { AgentDirectorateEnumeration, Enumerations } from "./types";

const isHeadedBySubordinateAgent = (headAgent: AgentBase, currentAgentId: UUID) =>
  headAgent.accessTreePath?.split(ACCESS_TREE_PATH_DELIMITER).includes(currentAgentId) ?? false;

/**
 * BE sends every directorate of the top agent, so the scoping happens here. Besides the subtree, the current
 * agent's own directorate is kept (its head agent may be a superior one) and so are `selectedIds`, so that an
 * already stored value is still rendered by its name.
 */
export const filterSubordinateAgentDirectorates = (
  directorates: AgentDirectorateEnumeration[],
  currentAgent?: AgentProfile,
  selectedIds?: UUID | UUID[],
) => {
  const keptIds = selectedIds ? [selectedIds].flat() : [];

  return directorates.filter(
    (directorate) =>
      keptIds.includes(directorate.id) ||
      (!!currentAgent &&
        (directorate.id === currentAgent.directorate?.id ||
          isHeadedBySubordinateAgent(directorate.headAgent, currentAgent.id))),
  );
};

/**
 * Permission definitions are left out: they are large and only admin screens need them, so
 * `useGetPermissionDefinitions` loads them on demand. The API sends every type when none is requested.
 */
export type PreloadedEnumerationType = Exclude<EnumerationType, EnumerationType.PERMISSION_DEFINITIONS>;

export const PRELOADED_ENUMERATION_TYPES = Object.values(EnumerationType).filter(
  (type): type is PreloadedEnumerationType => type !== EnumerationType.PERMISSION_DEFINITIONS,
);

/** The API is queried by type (`INSTITUTIONS`) but answers by field (`institutions`); this maps one to the other. */
export const ENUMERATION_FIELDS = {
  [EnumerationType.TOP_AGENTS]: "topAgents",
  [EnumerationType.AGENT_DIRECTORATES]: "agentDirectorates",
  [EnumerationType.AGENT_EDUCATIONS]: "agentEducations",
  [EnumerationType.INSTITUTIONS]: "institutions",
  [EnumerationType.PRODUCT_GROUPS]: "productGroups",
  [EnumerationType.PRODUCTS]: "products",
  [EnumerationType.AFFILIATE_PARTNERS]: "affiliatePartners",
  [EnumerationType.VEHICLE_COLORS]: "vehicleColors",
  [EnumerationType.COVERAGE_LIMITS]: "coverageLimits",
  [EnumerationType.COMPLICITIES]: "complicities",
  [EnumerationType.TARIFF_GROUPS]: "tariffGroups",
  [EnumerationType.COMMISSION_SOURCE_TEMPLATES]: "commissionSourceTemplates",
  [EnumerationType.COMMISSIONS_SETTINGS_LEVELS]: "commissionsSettingsLevels",
  [EnumerationType.COMMISSIONS_SETTINGS_RANKS]: "commissionsSettingsRanks",
  [EnumerationType.COMMISSIONS_BATCHES]: "commissionsBatches",
  [EnumerationType.BRANDING]: "branding",
  [EnumerationType.TOP_AGENT_ALLOWED_FEATURES]: "topAgentAllowedFeatures",
  [EnumerationType.CONTRACT_EXTERNAL_VIEW_INSTITUTIONS]: "contractExternalViewInstitutionIds",
} as const satisfies Record<PreloadedEnumerationType, keyof Enumerations>;

export type EnumerationField<T extends PreloadedEnumerationType> = (typeof ENUMERATION_FIELDS)[T];
